import { Alert, Button, Collapse, FormControl, FormLabel, IconButton, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useAuth } from "../AuthProvider";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { Error, Save } from "@mui/icons-material";
import './Profile.css';
import { updateEmail, updatePassword, updateProfile, verifyBeforeUpdateEmail } from "firebase/auth";
import CloseIcon from '@mui/icons-material/Close';
import { SetFullNameInUser } from "../FirebaseQueries";
import { red } from "@mui/material/colors";

export default function Profile() {
	const [name, setName] = useState("");
	const [nameDefault, setNameDefault] = useState("");
	const [nameError, setNameError] = useState(false);
	const [nameErrorMessage, setNameErrorMessage] = useState('');
	const [nameIsEditing, setNameIsEditing] = useState(false);
	const [nameIsLoading, setNameIsLoading] = useState(false);

	const [email, setEmail] = useState("");
	const [emailDefault, setEmailDefault] = useState("");
	const [emailNew, setEmailNew] = useState("");
	const [emailIsConfirming, setEmailIsConfirming] = useState(false);
	const [emailError, setEmailError] = useState(false);
	const [emailErrorMessage, setEmailErrorMessage] = useState('');
	const [emailIsEditing, setEmailIsEditing] = useState(false);
	const [emailIsLoading, setEmailIsLoading] = useState(false);

	const [password, setPassword] = useState("");
	const [passwordError, setPasswordError] = useState(false);
	const [passwordErrorMessages, setPasswordErrorMessages] = useState<string[]>([]);
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordIsEditing, setPasswordIsEditing] = useState(false);
	const [passwordIsLoading, setPasswordIsLoading] = useState(false);

	const auth = useAuth();

	useEffect(() => {
		setName(auth.currentUser?.displayName || '');
		setNameDefault(auth.currentUser?.displayName || '');
		setEmail(auth.currentUser?.email || '');
		setEmailDefault(auth.currentUser?.email || '');
	}, [])

	// ---------- State ---------- //
	// ---------- Computed Values ---------- //
	// ---------- Components ---------- //


	// ---------- Component Functions ---------- //
	const OnNameChanged = (event: ChangeEvent<HTMLTextAreaElement>) => setName(event.target.value);
	const OnEmailChanged = (event: ChangeEvent<HTMLTextAreaElement>) => setEmail(event.target.value);
	const OnPasswordChanged = (event: ChangeEvent<HTMLTextAreaElement>) => setPassword(event.target.value);
	const OnConfirmPasswordChanged = (event: ChangeEvent<HTMLTextAreaElement>) => setConfirmPassword(event.target.value);

	function HandleNameEditToggle() {
		if (nameIsEditing) {
			setName(nameDefault);
			setNameError(false);
			setNameErrorMessage('');
		}
		setNameIsEditing(!nameIsEditing);
	}

	function HandleEmailEditToggle() {
		if (emailIsEditing) {
			setEmail(emailDefault);
			setEmailError(false);
			setEmailErrorMessage('');
		}
		setEmailIsEditing(!emailIsEditing);
	}

	function HandlePasswordEditToggle() {
		if (passwordIsEditing) {
			setPassword("");
			setConfirmPassword("");
			setPasswordError(false);
			setPasswordErrorMessages([]);
		}
		setPasswordIsEditing(!passwordIsEditing);
	}

	function TrySubmitNameEdit() {
		if (!name || name.length < 1) {
			setNameError(true);
			setNameErrorMessage('Name is required.');
			return;
		} else {
			setNameError(false);
			setNameErrorMessage('');
		}

		if (name === nameDefault) {
			setNameIsLoading(false);
			setNameIsEditing(false);
			return;
		}

		setNameIsLoading(true);

		if (auth.currentUser !== null) {
			updateProfile(auth.currentUser, {
				displayName: name
			}).then(() => {
				// Profile updated!
				if (auth.currentUser !== null) {
					SetFullNameInUser(auth.currentUser.uid, name);
				}
				setNameIsLoading(false);
				setNameIsEditing(false);
				setNameDefault(name);
			}).catch((error) => {
				// An error occurred
				setNameIsLoading(false);
				setEmailError(true);
				setNameErrorMessage(error.message);
			});
		}
	}

	function TrySubmitEmailEdit() {
		if (!email || !/\S+@\S+\.\S+/.test(email)) {
			setEmailError(true);
			setEmailErrorMessage('Please enter a valid email address.');
			return;
		} else {
			setEmailError(false);
			setEmailErrorMessage('');
		}

		if (email === emailDefault) {
			setEmailIsLoading(false);
			setEmailIsEditing(false);
			return;
		}

		setEmailIsLoading(true);

		if (auth.currentUser !== null) {
			verifyBeforeUpdateEmail(auth.currentUser, email).then(() => {
				// Email updated!
				setEmailIsLoading(false);
				setEmailIsEditing(false);
				setEmailIsConfirming(true);
				setEmailNew(email);
				setEmail(emailDefault);
			}).catch((error) => {
				// An error occurred
				setEmailIsLoading(false);
				setEmailError(true);
				if (error.code === "auth/requires-recent-login") {
					setEmailErrorMessage("Email update requires recent login, please try again.");
				} else {
					setEmailErrorMessage(error.message);
				}

			});
		}
	}

	function TrySubmitPasswordEdit() {
		if (!password || password.length < 6) {
			setPasswordError(true);
			setPasswordErrorMessages(['Password must be at least 6 characters long.']);
			return;
		} else {
			if (password !== confirmPassword) {
				setPasswordError(true);
				setPasswordErrorMessages(['Password and Confirm Password must match.']);
				return;
			} else {
				setPasswordError(false);
				setPasswordErrorMessages(['']);
			}
		}
		setPasswordIsLoading(true);

		if (auth.currentUser !== null) {
			updatePassword(auth.currentUser, password).then(() => {
				// Update successful.
				setPasswordIsLoading(false);
				setPasswordIsEditing(false);
			}).catch((error) => {
				// An error ocurred
				setPasswordIsLoading(false);
				setPasswordError(true);
				if (error.code === "auth/requires-recent-login") {
					setPasswordErrorMessages(["Password update requires recent login, please try again."]);
				}
				else {
					const regex = /\[.*\]/g;
					const errorMessage: string[] | null = error.message.match(regex);
					const passwordErrors = errorMessage === null ? [] : errorMessage[0].replaceAll("[", "").replaceAll("]", "").split(", ");
					setPasswordErrorMessages(passwordErrors);
				}
			});
		}
	}


	return (
		<>
			<Typography component="h2" variant="h6" sx={{ mb: 2, textAlign: "left" }}>
				Profile
			</Typography>
			<Collapse in={emailIsConfirming} mountOnEnter unmountOnExit>
				<Alert icon={<Error fontSize="inherit" />} severity="error"
					action={
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => {
								setEmailIsConfirming(false);
							}}>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}>
					A verification email has been sent to {emailNew}. Please verify your new email to confirm changes, then refresh this page.
				</Alert>
			</Collapse>
			<Stack sx={{ pt: 4, gap: 4 }}>

				{/* ---------- Full Name ---------- */}
				<FormControl>
					<FormLabel sx={{ pl: 0.5, textAlign: 'left' }} htmlFor="name">Full name</FormLabel>
					<Stack direction={"row"} sx={{ gap: 1 }}>
						<TextField
							sx={{ maxWidth: '400px' }}
							disabled={!nameIsEditing || nameIsLoading}
							value={name}
							onChange={OnNameChanged}
							autoComplete="name"
							name="name"
							required
							fullWidth
							id="name"
							placeholder="John Smith"
							error={nameError}
							helperText={nameErrorMessage}
							color={nameError ? 'error' : 'primary'}
						/>
						<IconButton onClick={HandleNameEditToggle} disabled={nameIsLoading}>
							{nameIsEditing
								? <CancelIcon></CancelIcon>
								: <EditIcon></EditIcon>
							}
						</IconButton>
						{nameIsEditing &&
							<IconButton onClick={TrySubmitNameEdit} loading={nameIsLoading}>
								<Save></Save>
							</IconButton>
						}
					</Stack>
				</FormControl>
				{/* ---------- Email ---------- */}
				<FormControl>
					<FormLabel sx={{ pl: 0.5, textAlign: 'left' }} htmlFor="email">Email</FormLabel>
					<Stack direction={"row"} sx={{ gap: 1 }}>
						<TextField
							sx={{ maxWidth: '400px' }}
							disabled={!emailIsEditing}
							value={email}
							onChange={OnEmailChanged}
							required
							fullWidth
							id="email"
							placeholder="your@email.com"
							name="email"
							autoComplete="email"
							variant="outlined"
							error={emailError}
							helperText={emailErrorMessage}
							color={passwordError ? 'error' : 'primary'}
						/>
						<IconButton onClick={HandleEmailEditToggle} disabled={emailIsLoading}>
							{emailIsEditing
								? <CancelIcon></CancelIcon>
								: <EditIcon></EditIcon>
							}
						</IconButton>
						{emailIsEditing &&
							<IconButton onClick={TrySubmitEmailEdit} loading={emailIsLoading}>
								<Save></Save>
							</IconButton>
						}
					</Stack>
				</FormControl>
				<Stack sx={{ gap: 2 }}>
					{/* ---------- Password ---------- */}
					<FormControl>
						<FormLabel sx={{ pl: 0.5, textAlign: 'left' }} htmlFor="password">Password</FormLabel>
						<Stack direction={"row"} sx={{ gap: 1 }}>
							<TextField
								sx={{ maxWidth: '400px' }}
								disabled={!passwordIsEditing}
								value={password}
								onChange={OnPasswordChanged}
								required
								fullWidth
								name="password"
								placeholder="••••••"
								type="password"
								id="password"
								autoComplete="new-password"
								variant="outlined"
								error={passwordError}
								color={passwordError ? 'error' : 'primary'}
							/>
							<IconButton onClick={HandlePasswordEditToggle} disabled={passwordIsLoading}>
								{passwordIsEditing
									? <CancelIcon></CancelIcon>
									: <EditIcon></EditIcon>
								}
							</IconButton>
							{passwordIsEditing &&
								<IconButton onClick={TrySubmitPasswordEdit} loading={passwordIsLoading}>
									<Save></Save>
								</IconButton>
							}
						</Stack>
						<Stack spacing={"2px"} sx={{ marginTop: "8px" }} justifyContent={"flex-start"} alignItems={"flex-start"}>
							{passwordErrorMessages.map((message, index) => (
								<Typography color={red[700]} fontSize={12} key={index}>{message}</Typography>
							))}
						</Stack>
					</FormControl>
					{/* ---------- Confirm Password ---------- */}
					<FormControl>
						<FormLabel sx={{ pl: 0.5, textAlign: 'left' }} htmlFor="confirm-password">Confirm Password</FormLabel>
						<TextField
							sx={{ maxWidth: '400px' }}
							disabled={!passwordIsEditing}
							value={confirmPassword}
							onChange={OnConfirmPasswordChanged}
							required
							fullWidth
							name="confirm-password"
							placeholder="••••••"
							type="password"
							id="confirm-password"
							autoComplete="new-password"
							variant="outlined"
						/>
					</FormControl>
				</Stack>
			</Stack>
		</>
	)
}